/**
 * `/devices` — WP-K5, Task 45. `web/shapes/devices.md` is the agreed shape.
 *
 * This is the screen where someone takes a device away from a person. The two
 * consequential controls are revoke (`RevokeConfirm`) and minting a registration
 * token (`TokenReveal`); everything else exists so that those two are performed with
 * the right facts on screen.
 *
 * ── Four independent reads, four independent skeletons ──────────────────────
 *
 * The fleet page, the selected device, its health and the token list are four
 * requests. `PRODUCT.md` §4 and `web/shapes/my-activity.md` §5 make "skeleton per
 * region" an acceptance criterion, and `useApiQuery` is what makes it cheap.
 *
 * ── Deliberately not polled ─────────────────────────────────────────────────
 *
 * `usePolledQuery` exists (Task 44) and is not used here. This is a screen where a
 * human performs an action and then reads the result of *that* action; a background
 * refresh landing between reading the revoke inventory and pressing its button would
 * change the row under the operator's hands, and that inventory is the thing they are
 * consenting to. Reads re-run on an explicit refresh, and after each successful write
 * through `reloadToken`.
 *
 * ── The plaintext token: where it lives, and for how long ───────────────────
 *
 * `createToken.data` holds the `201` body, which contains the secret. The effect
 * below lifts it into a `Reveal` and **immediately calls `createToken.reset()`**, so
 * the mutation hook stops holding a plaintext the moment the panel has one — one
 * copy, in one variant of one union, with no inverse (`web/src/devices/reveal.ts`).
 * Dismissing burns it; navigating away unmounts it; nothing writes it anywhere.
 *
 * ── The filter and the selection live in the URL ────────────────────────────
 *
 * Same construction as Task 44's overview: `useSearchParams`, so the back button
 * walks the filter history and a pasted URL shows a colleague the same fleet. The
 * **selected device** is in the URL too, which is what makes "send me the machine you
 * mean" a link rather than a description. The token is not, and could not be — see
 * above.
 *
 * ── Nothing here reads `Device.online` ──────────────────────────────────────
 *
 * It is `liveness === "online"` and nothing more, so reading it would fold three
 * states into two. `web/src/api/types.ts` does not declare it, and
 * `devices.test.tsx` scans this whole directory for the identifier as a set
 * comparison over the files that exist.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';

import { useSession } from '../auth/SessionProvider';
import { useI18n } from '../i18n';
import {
  DEVICES_PATH,
  deviceHealthPath,
  devicePath,
  deviceTransitionPath,
  devicesAdminPath,
  registrationTokenRevokePath,
  registrationTokensPath,
  REGISTRATION_TOKENS_PATH,
} from '../api/paths';
import type {
  Device,
  DeviceAdminListResponse,
  DeviceApproveRequest,
  DeviceHealth,
  DeviceStatus,
  RegistrationTokenCreateRequest,
  RegistrationTokenCreated,
  RegistrationTokenListResponse,
} from '../api/types';
import { DEVICE_STATUSES } from '../api/types';
import { useApiMutation } from '../api/useApiMutation';
import { useApiQuery } from '../api/useApiQuery';
import { employeesPath, type EmployeeDirectoryList } from '../api/employees';
import { browserClipboard, burn, NO_REVEAL, reveal, type Reveal } from '../devices/reveal';
import { census } from '../devices/fleet';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { EmptyState } from '../states/EmptyState';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import { ScreenHeading } from '../shell/ScreenHeading';
import { DeviceDetail } from './devices/DeviceDetail';
import { DeviceFilters, hasAnyFilter, type DeviceFilterValues } from './devices/DeviceFilters';
import { DeviceHealthPanel } from './devices/DeviceHealthPanel';
import { DeviceTable } from './devices/DeviceTable';
import { FleetCensusStrip } from './devices/FleetCensus';
import { OffboardAdvice } from './devices/OffboardAdvice';
import { TokenPanel } from './devices/TokenPanel';
import { TokenReveal } from './devices/TokenReveal';

/**
 * One page, explicit rather than left at the endpoint's default.
 *
 * `GET /devices` is "a small, stable catalogue" and offset-paged, and the census
 * counts what arrived. A page boundary the screen did not choose is a page boundary
 * the reader cannot see, which is Task 43's ruling about `attendanceGridPath` applied
 * to a much smaller list.
 */
export const DEVICE_PAGE_LIMIT = 100;
export const TOKEN_PAGE_LIMIT = 100;

/** `OffsetLimit`'s maximum — one page of the directory is the name lookup below. */
export const ROSTER_PAGE_LIMIT = 200;

function statusFromParam(raw: string | null): DeviceStatus | null {
  return DEVICE_STATUSES.find((value) => value === raw) ?? null;
}

export function DevicesScreen(): ReactNode {
  const { t } = useI18n();
  const { user } = useSession();
  const [params, setParams] = useSearchParams();
  const timezone = user?.timezone ?? 'Asia/Ho_Chi_Minh';

  const filters: DeviceFilterValues = useMemo(
    () => ({
      status: statusFromParam(params.get('status')),
      q: params.get('q'),
      employeeId: params.get('employee_id'),
      departmentId: params.get('department_id'),
    }),
    [params],
  );
  const selectedId = params.get('device');

  /**
   * A **bump**, not a cache-buster in the query string. `useApiQuery` takes it as a
   * parameter for exactly this reason: an invented query value would travel to the
   * server, where the list endpoints parse strictly, and "the client refreshed" is
   * not a fact the server should have an opinion about.
   */
  const [reloadToken, setReloadToken] = useState(0);
  const bump = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  const [tokenReveal, setTokenReveal] = useState<Reveal>(NO_REVEAL);

  const listPath = devicesAdminPath({
    limit: DEVICE_PAGE_LIMIT,
    status: filters.status ?? undefined,
    q: filters.q ?? undefined,
    employeeId: filters.employeeId ?? undefined,
    departmentId: filters.departmentId ?? undefined,
  });

  const list = useApiQuery<DeviceAdminListResponse>(listPath, reloadToken);
  const detail = useApiQuery<Device>(
    selectedId === null ? null : devicePath(selectedId),
    reloadToken,
  );
  const health = useApiQuery<DeviceHealth>(
    selectedId === null ? null : deviceHealthPath(selectedId),
    reloadToken,
  );
  const tokens = useApiQuery<RegistrationTokenListResponse>(
    registrationTokensPath(TOKEN_PAGE_LIMIT),
    reloadToken,
  );

  /**
   * The names behind `Device.employee_id` — Luật Không In Mã Máy, fix round 2 (R3).
   *
   * `GET /devices` publishes the employee's **achau** id and nothing else about the
   * person (`admin-repo.ts` selects `e.achau_user_id`), so the fleet table printed
   * `990103` where a name belongs. `GET /employees` publishes both ids beside the
   * name, which makes one directory read the whole translation.
   *
   * Its own permission gate is the `workforce.view_*` ladder, not `devices.*`, so an
   * IT administrator holding only the device codes gets a refusal here — a real,
   * ordinary case, and the reason `DeviceEmployee` has a no-name branch rather than
   * an error one. `useApiQuery` keeps the refusal to itself; the column simply says
   * "Chưa gán tên" and keeps the id where a machine can read it.
   *
   * ponytail: one 200-row page (the contract's `OffsetLimit` maximum), not a lookup
   * per device — a company past 200 employees gets the no-name branch for the tail.
   * Upgrade path: publish the name on `Device` itself, where `admin-repo.ts` already
   * LEFT JOINs the row it would come from.
   */
  const roster = useApiQuery<EmployeeDirectoryList>(
    employeesPath({ status: 'all', limit: ROSTER_PAGE_LIMIT }),
  );
  const employeeNames = useMemo(() => {
    const byAchauId = new Map<string, string>();
    for (const row of roster.data?.employees ?? []) byAchauId.set(row.achau_user_id, row.full_name);
    return byAchauId;
  }, [roster.data]);

  const createToken = useApiMutation<RegistrationTokenCreateRequest, RegistrationTokenCreated>(
    REGISTRATION_TOKENS_PATH,
  );
  const tokenRevoke = useApiMutation<{ reason: string }, unknown>(REGISTRATION_TOKENS_PATH);

  /**
   * The two device transitions.
   *
   * Bound to `DEVICES_PATH` while nothing is selected. That branch is unreachable
   * rather than merely unlikely: both are called only from inside `DeviceDetail`,
   * which the tree below renders only once `detail.data` is non-null, which requires
   * `selectedId`. The fallback is a real, valid path rather than an empty string so
   * that a future caller who breaks that invariant gets a `405`-shaped failure it can
   * see, instead of a request to `/api/v1/devices/`.
   */
  const approve = useApiMutation<DeviceApproveRequest, Device>(
    selectedId === null ? DEVICES_PATH : deviceTransitionPath(selectedId, 'approve'),
  );
  const revokeDevice = useApiMutation<{ reason: string }, Device>(
    selectedId === null ? DEVICES_PATH : deviceTransitionPath(selectedId, 'revoke'),
  );

  /**
   * Lift the secret out of the mutation hook and clear the hook in the same tick.
   *
   * Depends on `created` and `resetCreate`, not on the mutation object: that object
   * is a fresh literal on every render and would loop. After `resetCreate()`,
   * `created` is `null`, this runs once more and returns — so the plaintext exists in
   * `createToken.data` for exactly one commit and in `tokenReveal` until it is burned.
   */
  const created = createToken.data;
  const resetCreate = createToken.reset;
  useEffect(() => {
    if (created === null) return;
    setTokenReveal(
      reveal({
        tokenId: created.token_id,
        tokenPrefix: created.token_prefix,
        token: created.token,
        enrollmentUrl: created.enrollment_url ?? null,
        expiresAt: created.expires_at,
        maxUses: created.max_uses,
      }),
    );
    resetCreate();
  }, [created, resetCreate]);

  const setParam = useCallback(
    (updates: Readonly<Record<string, string | null>>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      setParams(next);
    },
    [params, setParams],
  );

  const applyFilters = useCallback(
    (values: DeviceFilterValues) => {
      setParam({
        status: values.status,
        q: values.q,
        employee_id: values.employeeId,
        department_id: values.departmentId,
        // A filter applied while a machine is selected would leave the detail open on
        // a row the table no longer shows.
        device: null,
      });
    },
    [setParam],
  );

  const devices = list.data?.data ?? [];
  const total = list.data?.page.total ?? devices.length;
  const fleetCensus = census(devices);

  return (
    <div className="wp-stack wp-c-screen">
      <ScreenHeading>{t('nav.devices')}</ScreenHeading>
      <p className="wp-small wp-muted">{t('devices.intro')}</p>

      {list.status === 'loading' ? (
        <Skeleton rows={5} variant="block" label={t('devices.subject')} testId="k5-list-skeleton" />
      ) : null}
      {list.status === 'error' && list.error !== null ? (
        <ApiErrorNote
          error={list.error}
          onRetry={list.reload}
          subject={t('devices.subject')}
          testId="k5-list-error"
        />
      ) : null}

      {list.data === null ? null : (
        <>
          <FleetCensusStrip census={fleetCensus} total={total} />
          <DeviceFilters values={filters} onApply={applyFilters} onReload={bump} />

          {devices.length === 0 ? (
            hasAnyFilter(filters) ? (
              <EmptyState
                title={t('devices.emptyFiltered.title')}
                description={t('devices.emptyFiltered.body')}
                testId="k5-empty-filtered"
              />
            ) : (
              <EmptyState
                title={t('devices.empty.title')}
                description={t('devices.empty.body')}
                testId="k5-empty"
              />
            )
          ) : (
            <DeviceTable
              employeeNames={employeeNames}
              devices={devices}
              total={total}
              timezone={timezone}
              selectedId={selectedId}
              onSelect={(deviceId) => {
                approve.reset();
                revokeDevice.reset();
                setParam({ device: deviceId });
              }}
            />
          )}
        </>
      )}

      {selectedId === null ? (
        <p className="wp-muted" data-testid="k5-detail-none">
          {t('devices.detail.select')}
        </p>
      ) : (
        <>
          {detail.status === 'loading' ? (
            <Skeleton rows={4} label={t('devices.detail.subject')} testId="k5-detail-skeleton" />
          ) : null}
          {detail.status === 'error' && detail.error !== null ? (
            <ApiErrorNote
              error={detail.error}
              onRetry={detail.reload}
              subject={t('devices.detail.subject')}
              testId="k5-detail-error"
            />
          ) : null}
          {detail.data === null ? null : (
            <DeviceDetail
              employeeNames={employeeNames}
              device={detail.data}
              health={health.data}
              timezone={timezone}
              approvePending={approve.status === 'pending'}
              revokePending={revokeDevice.status === 'pending'}
              onApprove={(body) => {
                void approve.submit(body).then(bump);
              }}
              onRevoke={(reason) => {
                void revokeDevice.submit({ reason }).then(bump);
              }}
              healthSlot={
                <>
                  {health.status === 'loading' ? (
                    <Skeleton
                      rows={3}
                      label={t('devices.health.subject')}
                      testId="k5-health-skeleton"
                    />
                  ) : null}
                  {health.status === 'error' && health.error !== null ? (
                    <ApiErrorNote
                      error={health.error}
                      onRetry={health.reload}
                      subject={t('devices.health.subject')}
                      testId="k5-health-error"
                    />
                  ) : null}
                  {health.data === null ? null : (
                    <DeviceHealthPanel
                      health={health.data}
                      deviceLabel={detail.data.hostname ?? detail.data.device_id}
                      timezone={timezone}
                    />
                  )}
                </>
              }
              approveOutcome={
                <>
                  {approve.status === 'ok' ? (
                    <StatusNote
                      severity="low"
                      title={t('devices.approve.done')}
                      testId="k5-approve-done"
                    />
                  ) : null}
                  {approve.status === 'error' && approve.error !== null ? (
                    <ApiErrorNote
                      error={approve.error}
                      subject={t('devices.approve.title')}
                      testId="k5-approve-error"
                    />
                  ) : null}
                </>
              }
              revokeOutcome={
                <>
                  {revokeDevice.status === 'ok' ? (
                    <StatusNote
                      severity="high"
                      role="alert"
                      title={t('devices.revoke.done')}
                      testId="k5-revoke-done"
                    />
                  ) : null}
                  {revokeDevice.status === 'error' && revokeDevice.error !== null ? (
                    <ApiErrorNote
                      error={revokeDevice.error}
                      subject={t('devices.revoke.open')}
                      testId="k5-revoke-error"
                    />
                  ) : null}
                </>
              }
            />
          )}
        </>
      )}

      {tokens.status === 'loading' ? (
        <Skeleton rows={3} label={t('devices.tokens.subject')} testId="k5-tokens-skeleton" />
      ) : null}
      {tokens.status === 'error' && tokens.error !== null ? (
        <ApiErrorNote
          error={tokens.error}
          onRetry={tokens.reload}
          subject={t('devices.tokens.subject')}
          testId="k5-tokens-error"
        />
      ) : null}
      {tokens.data === null ? null : (
        <TokenPanel
          tokens={tokens.data.data}
          timezone={timezone}
          creating={createToken.status === 'pending'}
          onCreate={(body) => {
            void createToken.submit(body).then(bump);
          }}
          onRevokeToken={(tokenId, reason) => {
            // The one write whose target is chosen per press: the token id comes off
            // the row, not off the mount. Hence `submit`'s optional path.
            void tokenRevoke.submit({ reason }, registrationTokenRevokePath(tokenId)).then(bump);
          }}
          revealSlot={
            <TokenReveal
              state={tokenReveal}
              onDismiss={() => {
                setTokenReveal(burn);
              }}
              clipboard={browserClipboard()}
            />
          }
          createOutcome={
            createToken.status === 'error' && createToken.error !== null ? (
              <ApiErrorNote
                error={createToken.error}
                subject={t('devices.tokens.subject')}
                testId="k5-token-create-error"
              />
            ) : null
          }
          revokeOutcome={
            tokenRevoke.status === 'ok' ? (
              <StatusNote
                severity="info"
                title={t('devices.tokens.revokeDone')}
                testId="k5-token-revoke-done"
              />
            ) : tokenRevoke.status === 'error' && tokenRevoke.error !== null ? (
              <ApiErrorNote
                error={tokenRevoke.error}
                subject={t('devices.tokens.subject')}
                testId="k5-token-revoke-error"
              />
            ) : null
          }
        />
      )}

      <OffboardAdvice
        employeeId={filters.employeeId}
        onLookup={(employeeId) => {
          applyFilters({ ...filters, employeeId });
        }}
      />
    </div>
  );
}
